import { FC, useContext } from 'react';
import {
  IconTransfers,
  useYupValidationResolver,
} from '@vs-online-only-app/ui';
import styled from 'styled-components';
import * as yup from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import {
  IconButton,
  Typography,
  Dialog,
  Stack,
  Alert,
  DialogContent,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { AssociatesList } from './AssociatesList';
import AssociateFilters from './AssociateFilters';
import { useSelectedHeroConversation } from '../../../hooks/useSelectedHeroConversation';
import OnlineIndicator from './OnlineIndicator';
import { TransferContext, TransferProvider } from './TransferContext';
import TransferModalNote from './TransferModalNote';
import TransferModalButtons from './TransferModalButtons';

const CloseButton = styled(IconButton)`
  position: absolute;
  top: ${({ theme }) => theme.spacing(1.5)};
  right: ${({ theme }) => theme.spacing(1.5)};
`;

export type TransferFormFieldValues = {
  note: string;
  selectedAssociateIds: string[];
};

const validationSchema = yup.object({
  note: yup.string().required('A note is required'),
  selectedAssociateIds: yup.array().of(yup.string()).min(1).required(),
});

type Props = {
  onClose: () => void;
};

const SelectedConversationTransferModal: FC<Props> = ({ onClose }) => {
  const conversation = useSelectedHeroConversation();

  const { error } = useContext(TransferContext);

  const formMethods = useForm<TransferFormFieldValues>({
    defaultValues: {
      note: '',
      selectedAssociateIds: [],
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: useYupValidationResolver(validationSchema),
  });

  const { errors: formErrors } = formMethods.formState;

  if (!conversation) {
    return null;
  }

  return (
    <FormProvider {...formMethods}>
      <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
        <CloseButton onClick={onClose}>
          <Close />
        </CloseButton>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <Stack direction="row" alignItems="center" gap={1} pb={1}>
            <IconTransfers />
            <Typography fontWeight="bold">
              Transfer {conversation.conversationStarter.firstName}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <AssociateFilters />
            <OnlineIndicator />
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
          {formErrors?.root && (
            <Alert severity="error">{formErrors.root.message}</Alert>
          )}
          {formErrors?.selectedAssociateIds && (
            <Alert severity="error">
              Please select at least one team member in order to request
              transfer.
            </Alert>
          )}
          <AssociatesList />
          <TransferModalNote />
        </DialogContent>
        <TransferModalButtons onClose={onClose} />
      </Dialog>
    </FormProvider>
  );
};

const SelectedConversationTransferModalWrapper: FC<Props> = (props) => (
  <TransferProvider>
    <SelectedConversationTransferModal {...props} />
  </TransferProvider>
);

export default SelectedConversationTransferModalWrapper;
